/**
 * ui/components/game/line-view-model.ts
 *
 * Pure helpers for the line view (one row or column at a time).
 */

import type { LineRef } from '../../../engine/types';
import { hintedLines, type HintAssists } from '../../../engine/selectors';

export type LineAxis = 'row' | 'column';

export interface LineRefView {
  axis: LineAxis;
  line: number;
}

/**
 * Where ‹ (d = -1) or › (d = 1) goes: the neighbouring line, or past the last
 * row on to column 1 (and past the last column back to row 1); ‹ runs the other
 * way.
 */
export function lineStepTarget(current: LineRefView, d: -1 | 1, rows: number, columns: number): LineRefView {
  const count = current.axis === 'row' ? rows : columns;
  const next = current.line + d;
  if (next >= 0 && next < count) return { axis: current.axis, line: next };
  const axis: LineAxis = current.axis === 'row' ? 'column' : 'row';
  const otherCount = axis === 'row' ? rows : columns;
  return { axis, line: d > 0 ? 0 : otherCount - 1 };
}

/** Number of cells in a line of this axis. */
export function lineLength(axis: LineAxis, rows: number, columns: number): number {
  return axis === 'row' ? columns : rows;
}

/** Number of lines of this axis. */
export function lineCount(axis: LineAxis, rows: number, columns: number): number {
  return axis === 'row' ? rows : columns;
}

/** Board index of cell `pos` in a line. */
export function lineCellIndex(axis: LineAxis, line: number, pos: number, columns: number): number {
  return axis === 'row' ? line * columns + pos : pos * columns + line;
}

/** The line and position of a cell, seen from an axis. */
export function lineOfCell(axis: LineAxis, cell: { row: number; column: number }): { line: number; pos: number } {
  return axis === 'row' ? { line: cell.row, pos: cell.column } : { line: cell.column, pos: cell.row };
}

/** The board cell at `pos` in a line. */
export function cellOfLine(axis: LineAxis, line: number, pos: number): { row: number; column: number } {
  return axis === 'row' ? { row: line, column: pos } : { row: pos, column: line };
}

/**
 * Cells per wrapped chunk: everything when it fits at `minCell`, otherwise a
 * multiple of 5 (so the 5-cell grid stays aligned), balanced across chunks
 * (30 cells → 15 + 15, not 25 + 5).
 */
export function wrapWidth(n: number, available: number, minCell: number): number {
  const w = Math.floor(available / minCell);
  if (w >= n) return n;
  const max5 = Math.max(5, Math.floor(w / 5) * 5);
  const chunks = Math.ceil(n / max5);
  return Math.min(max5, Math.ceil(n / chunks / 5) * 5);
}

/**
 * Cell positions in [from, to) that get a number above them: every 5th cell
 * and the line's last cell. Each label is the 1-based position (5, 10, …).
 */
export function tickLabels(from: number, to: number, length: number): number[] {
  const out: number[] = [];
  for (let p = from; p < to; p++) {
    if ((p + 1) % 5 === 0 || p === length - 1) out.push(p);
  }
  return out;
}

/** The run of equal cells through `pos` (the whole line, however it wraps). */
export function lineRun(cells: readonly number[], pos: number): { value: number; length: number; start: number; end: number } {
  const value = cells[pos];
  let start = pos;
  while (start > 0 && cells[start - 1] === value) start--;
  let end = pos;
  while (end < cells.length - 1 && cells[end + 1] === value) end++;
  return { value, length: end - start + 1, start, end };
}

export interface LineFlagInput {
  clueState: { rows: readonly { isSolvable: boolean }[]; columns: readonly { isSolvable: boolean }[] };
  assists: HintAssists;
}

/**
 * The footer flag for a line: 'error' when it conflicts with its clues, else
 * 'hint' when a hint points at it (shown line hints or an explicit hint).
 */
export function lineFlag(s: LineFlagInput, axis: LineAxis, line: number): 'error' | 'hint' | null {
  const state = axis === 'row' ? s.clueState.rows[line] : s.clueState.columns[line];
  if (state && !state.isSolvable) return 'error';
  return hintedLines(s.assists).some((ref) => ref.axis === axis && ref.index === line) ? 'hint' : null;
}

/**
 * The next line with a hint after `current`, in ‹ › order (rows, then
 * columns, then back to row 1). Null when no other line has one.
 */
export function nextHintLine(current: LineRefView, hints: Iterable<LineRef>, rows: number, columns: number): LineRefView | null {
  const total = rows + columns;
  const order = (axis: LineAxis, line: number) => (axis === 'row' ? line : rows + line);
  const from = order(current.axis, current.line);
  let best: LineRefView | null = null;
  let bestGap = total;
  for (const ref of hints) {
    if (ref.index < 0 || ref.index >= lineCount(ref.axis, rows, columns)) continue;
    const gap = (order(ref.axis, ref.index) - from + total) % total;
    if (gap > 0 && gap < bestGap) {
      bestGap = gap;
      best = { axis: ref.axis, line: ref.index };
    }
  }
  return best;
}

/** Cell sizes when fitting (no zoom set): as large as fits, within these. */
export const LINE_FIT_MIN_CELL_PX = 16;
export const LINE_FIT_MAX_CELL_PX = 40;
/** Zoom range for the line view's cell size. */
export const LINE_ZOOM_MIN_CELL_PX = 10;
export const LINE_ZOOM_MAX_CELL_PX = 64;

export function clampLineCellPx(px: number): number {
  return Math.min(LINE_ZOOM_MAX_CELL_PX, Math.max(LINE_ZOOM_MIN_CELL_PX, px));
}

/**
 * Cells per chunk and cell size for a line `length` cells long in `available`
 * px, keeping one more cell's width for the footer slot. Without a zoom
 * (`cellPx` null) cells are as large as fits (16–40px); with one, cells are
 * that size and as many fit per chunk as the width allows, so zooming out puts
 * more of the line on each chunk.
 */
export function lineLayout(length: number, available: number, cellPx: number | null): { width: number; cellPx: number } {
  const target = cellPx === null ? LINE_FIT_MIN_CELL_PX : clampLineCellPx(cellPx);
  const max = cellPx === null ? LINE_FIT_MAX_CELL_PX : target;
  const width = wrapWidth(length, available - target, target);
  // Whole pixels, so cell borders stay crisp at any zoom.
  return { width, cellPx: Math.max(1, Math.floor(Math.min(max, available / (width + 1)))) };
}

/** Vertical touchpad travel, in cells, per chunk jumped. */
export const PAD_CHUNK_STEP_CELLS = 2;
/** Least vertical touchpad travel, in px, per chunk jumped. */
export const PAD_CHUNK_STEP_MIN_PX = 40;
/** Smallest cell size the touchpad's travel is based on, however far the view zooms out. */
export const PAD_MIN_CELL_PX = 16;

/** Finger travel, in px, per cell sideways and per chunk up/down. */
export interface PadTravel {
  cellPx: number;
  chunkPx: number;
}

/**
 * Touchpad finger travel for a cell size and sensitivity. Cells smaller than
 * PAD_MIN_CELL_PX (zoomed out) count as that size, so the touchpad does not
 * turn twitchy as the cells shrink; a chunk jump always takes at least
 * PAD_CHUNK_STEP_MIN_PX, so a sideways drag's drift does not jump.
 */
export function padTravel(cellPx: number, sensitivity: number): PadTravel {
  const cell = Math.max(cellPx, PAD_MIN_CELL_PX) * sensitivity;
  return { cellPx: cell, chunkPx: Math.max(cell * PAD_CHUNK_STEP_CELLS, PAD_CHUNK_STEP_MIN_PX) };
}

/**
 * Touchpad cursor movement over a wrapped line, from a finger movement of
 * (dx, dy) px. Sideways movement runs along the whole line, so dragging past
 * the end of a chunk carries on into the next one (and before its start back
 * into the previous one), and a single stroke can cross chunks. Up/down jumps
 * whole chunks, one per `travel.chunkPx`. Returns the position along the line.
 */
export function padCursor(startPos: number, dx: number, dy: number, travel: PadTravel, width: number, length: number): number {
  const chunks = Math.trunc(dy / travel.chunkPx);
  const pos = startPos + Math.round(dx / travel.cellPx) + chunks * width;
  return Math.min(Math.max(pos, 0), length - 1);
}

/**
 * How far to scroll (positive = down) to bring a target into a scrolling
 * view with `margin` px to spare: nothing when it is already in, otherwise
 * just far enough, showing the target's top when it is taller than the view.
 */
export function revealScrollDelta(
  view: { top: number; bottom: number },
  target: { top: number; bottom: number },
  margin: number,
): number {
  const top = view.top + margin;
  const bottom = view.bottom - margin;
  if (target.top < top || target.bottom - target.top > bottom - top) return target.top - top;
  if (target.bottom > bottom) return target.bottom - bottom;
  return 0;
}
